import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { mcGetTokenAnalytics } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Search, Loader2, Activity, TrendingUp, TrendingDown, Users, Lock, ExternalLink, ArrowRight,
} from 'lucide-react';

const fmtPrice = (v) => {
  const n = parseFloat(v);
  if (v == null || !isFinite(n) || n <= 0) return '—';
  if (n < 0.000001) return `$${n.toExponential(2)}`;
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
};
const fmtUsd = (v) => {
  const n = parseFloat(v);
  if (!isFinite(n) || n === 0) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtBase = (v) => {
  const n = parseFloat(v);
  if (!isFinite(n) || n === 0) return '0';
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(2);
};
const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '—');

const Stat = ({ label, value, sub, tone }) => (
  <div className="border border-border bg-card p-3">
    <div className="mono-label text-[10px]">{label}</div>
    <div className={`text-xl font-bold font-mono mt-1 ${tone || ''}`}>{value}</div>
    {sub != null && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

function Change({ v }) {
  const n = parseFloat(v);
  if (!isFinite(n)) return <span className="text-muted-foreground">—</span>;
  const up = n >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 ${up ? 'text-success' : 'text-destructive'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {n.toFixed(2)}%
    </span>
  );
}

function PressureBar({ pressure }) {
  const buys = pressure.buys || 0;
  const sells = pressure.sells || 0;
  const totalCt = buys + sells;
  const buyPct = totalCt ? (buys / totalCt) * 100 : 50;
  const bv = pressure.buy_volume_usd || 0;
  const sv = pressure.sell_volume_usd || 0;
  const totalVol = bv + sv;
  const buyVolPct = totalVol ? (bv / totalVol) * 100 : 50;
  const sentimentTone = pressure.sentiment === 'bullish' ? 'text-success border-success/50'
    : pressure.sentiment === 'bearish' ? 'text-destructive border-destructive/50'
    : 'text-muted-foreground border-border';
  return (
    <Card data-testid="mc-pressure-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2"><Activity className="w-4 h-4" /> Buy / Sell Pressure</span>
          <Badge variant="outline" className={`uppercase text-[10px] ${sentimentTone}`} data-testid="mc-sentiment">
            {pressure.sentiment}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Trades (24h) · {buys} buys</span><span>{sells} sells</span>
          </div>
          <div className="h-2.5 w-full rounded-full overflow-hidden bg-destructive/30 flex">
            <div className="h-full bg-success" style={{ width: `${buyPct}%` }} data-testid="mc-pressure-count-bar" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">By count: <span className="font-mono text-foreground">{pressure.by_count}%</span></div>
        </div>
        <div>
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>Volume · {fmtUsd(bv)} bought</span><span>{fmtUsd(sv)} sold</span>
          </div>
          <div className="h-2.5 w-full rounded-full overflow-hidden bg-destructive/30 flex">
            <div className="h-full bg-success" style={{ width: `${buyVolPct}%` }} data-testid="mc-pressure-vol-bar" />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">By volume: <span className="font-mono text-foreground">{pressure.by_volume}%</span> <span className="opacity-60">(from recent pool trades)</span></div>
        </div>
      </CardContent>
    </Card>
  );
}

function UnavailableCard({ title, reason }) {
  return (
    <Card className="border-dashed" data-testid={`mc-unavailable-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
          <Lock className="w-4 h-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground leading-relaxed">{reason}</CardContent>
    </Card>
  );
}

export default function McTokenAnalytics({ chain, chainInfo }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const label = chainInfo?.label || chain;

  const load = useCallback(async (addr) => {
    const a = (addr || '').trim();
    if (!a) return;
    setLoading(true); setError(null);
    try {
      const d = await mcGetTokenAnalytics(chain, a);
      setData(d);
      if (!d.indexed) setError('No trading pairs found for this token on ' + label + '.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [chain, label]);

  // Auto-load when a ?token= is present (shareable links / prefilled).
  useEffect(() => {
    const t = searchParams.get('token');
    if (t) { setToken(t); load(t); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain]);

  const submit = (e) => {
    e.preventDefault();
    const a = token.trim();
    const p = new URLSearchParams(searchParams);
    if (a) p.set('token', a); else p.delete('token');
    setSearchParams(p, { replace: true });
    load(a);
  };

  const m = data?.market || {};
  const traders = data?.top_traders || [];

  return (
    <div className="space-y-6" data-testid="mc-analytics">
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="mc-analytics-form">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="mc-token-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={`Token contract on ${label}…`}
            className="pl-10 font-mono text-sm h-11"
          />
        </div>
        <Button type="submit" className="h-11 px-6" disabled={loading || !token.trim()} data-testid="mc-analytics-load-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Analyze <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </form>

      {error && (
        <div className="border border-destructive/40 bg-destructive/10 text-destructive text-sm p-3 rounded" data-testid="mc-analytics-error">
          {error}
        </div>
      )}

      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      )}

      {!loading && !data && !error && (
        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground" data-testid="mc-analytics-empty">
          Paste a token contract above to see live market analytics, buy/sell pressure and recent top traders on <span className="text-foreground font-medium">{label}</span>.
        </div>
      )}

      {data && data.indexed && (
        <>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold" data-testid="mc-analytics-token-symbol">{data.symbol || shortAddr(data.address)}</h2>
            {data.name && <span className="text-sm text-muted-foreground">{data.name}</span>}
            <Link to={`/explorer/${chain}/token/${data.address}`} className="ml-auto text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="mc-analytics-token-link">
              Token page <ExternalLink className="w-3 h-3" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="mc-market-grid">
            <Stat label="Price" value={fmtPrice(m.price_usd)} />
            <Stat label="1h" value={<Change v={m.change_h1} />} />
            <Stat label="24h" value={<Change v={m.change_h24} />} />
            <Stat label="Market Cap" value={fmtUsd(m.market_cap || m.fdv)} />
            <Stat label="Liquidity" value={fmtUsd(m.liquidity_usd)} />
            <Stat label="24h Volume" value={fmtUsd(m.volume_24h)} sub={`${m.pool_count || 0} pool(s)`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PressureBar pressure={data.pressure} />
            <div className="grid grid-cols-1 gap-3">
              <UnavailableCard
                title="Holder Distribution"
                reason={`Whale/holder tiers need a full on-chain holder index. Public APIs on ${label} don't expose the complete holder set — this forensic view is available on Krown, which we index ourselves.`}
              />
              <UnavailableCard
                title="Wallet Relationship Graph"
                reason={`Transfer-flow mapping needs full historical transfers. On ${label} we use recent DEX trades only, so the wallet graph is Krown-only for now.`}
              />
            </div>
          </div>

          <Card data-testid="mc-top-traders-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span className="flex items-center gap-2"><Users className="w-4 h-4" /> Recent Top Traders</span>
                {data.trades_window && (
                  <span className="text-[10px] text-muted-foreground font-normal" data-testid="mc-trades-window">
                    from last {data.trades_window.count} pool trades
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {traders.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No recent trades found for this token.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]" data-testid="mc-top-traders-table">
                    <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">#</th>
                        <th className="text-left px-3 py-2 font-medium">Wallet</th>
                        <th className="text-right px-3 py-2 font-medium">Volume</th>
                        <th className="text-right px-3 py-2 font-medium">Trades</th>
                        <th className="text-right px-3 py-2 font-medium">Net {data.symbol || 'base'}</th>
                        <th className="text-left px-3 py-2 font-medium">Side</th>
                      </tr>
                    </thead>
                    <tbody>
                      {traders.map((t) => (
                        <tr key={t.address} className="border-b border-border/60 hover:bg-muted/30" data-testid={`mc-trader-row-${t.address}`}>
                          <td className="px-3 py-2 text-muted-foreground">{t.rank}</td>
                          <td className="px-3 py-2">
                            <Link to={`/explorer/${chain}/address/${t.address}`} className="font-mono text-xs text-primary hover:underline">
                              {shortAddr(t.address)}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{fmtUsd(t.volume_usd)}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            <span className="text-success">{t.buys}</span>/<span className="text-destructive">{t.sells}</span>
                          </td>
                          <td className={`px-3 py-2 text-right font-mono ${t.is_net_buyer ? 'text-success' : 'text-destructive'}`}>
                            {t.is_net_buyer ? '+' : ''}{fmtBase(t.net_base)}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className={`text-[10px] ${t.is_net_buyer ? 'text-success border-success/50' : 'text-destructive border-destructive/50'}`}>
                              {t.is_net_buyer ? 'NET BUY' : 'NET SELL'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
